import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { Calculator, Users, Plus, Shield, Check, RefreshCw } from 'lucide-react';

export function TravelTeamBudgetPlanner() {
  const [numTournaments, setNumTournaments] = useState('6');
  const [avgTourneyFee, setAvgTourneyFee] = useState('600');
  const [rosterSize, setRosterSize] = useState('12');
  const [uniformCost, setUniformCost] = useState('150');
  const [facilityRent, setFacilityRent] = useState('2000');
  const [coachStipend, setCoachStipend] = useState('1500');
  const [fundraisingGoal, setFundraisingGoal] = useState('2000');

  // Parsed numbers
  const tourneys = parseInt(numTournaments) || 0;
  const tourneyFee = parseFloat(avgTourneyFee) || 0;
  const players = parseInt(rosterSize) || 1;
  const uniforms = parseFloat(uniformCost) || 0;
  const rent = parseFloat(facilityRent) || 0;
  const coach = parseFloat(coachStipend) || 0;
  const fundGoal = parseFloat(fundraisingGoal) || 0;

  // Modeler Calculations
  const tourneyExpense = tourneys * tourneyFee;
  const uniformsExpense = players * uniforms;
  const totalExpense = tourneyExpense + uniformsExpense + rent + coach;
  const netExpense = Math.max(0, totalExpense - fundGoal);

  const perPlayerFee = players > 0 ? netExpense / players : 0;

  // Break-even player table (different roster sizes)
  const rosterSizesToCompare = [10, 12, 14, 16];
  const breakEvenScenarios = useMemo(() => {
    return rosterSizesToCompare.map(size => {
      const sizeUniformExpense = size * uniforms;
      const sizeTotalExpense = tourneyExpense + sizeUniformExpense + rent + coach;
      const sizeNetExpense = Math.max(0, sizeTotalExpense - fundGoal);
      const sizePerPlayerFee = sizeNetExpense / size;
      return {
        size,
        total: sizeTotalExpense,
        perPlayer: sizePerPlayerFee
      };
    });
  }, [tourneyExpense, uniforms, rent, coach, fundGoal]);

  const loadSample = () => {
    setNumTournaments('6');
    setAvgTourneyFee('600');
    setRosterSize('12');
    setUniformCost('150');
    setFacilityRent('2400');
    setCoachStipend('1200');
    setFundraisingGoal('2500');
  };

  const clearAll = () => {
    setNumTournaments('0');
    setAvgTourneyFee('0');
    setRosterSize('10');
    setUniformCost('0');
    setFacilityRent('0');
    setCoachStipend('0');
    setFundraisingGoal('0');
  };

  return (
    <div className="space-y-8">
      {/* Intro Banner */}
      <div className="bg-[#0044FF] border-4 border-black text-white p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">Youth Travel Team Season Budget Modeler</h3>
        <p className="font-bold text-sm max-w-xl text-blue-100 dark:text-gray-300">
          Model total operation costs, equipment package options, and sponsorship offsets to establish player dues cards.
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSample} variant="secondary" className="bg-[#FFD600] text-black hover:bg-white border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-white text-white hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Reset
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 text-sm">
        
        {/* Sliders Form */}
        <div className="lg:col-span-1 bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
          <h4 className="font-display font-black text-xl uppercase tracking-tight">Cost Inputs</h4>
          
          <div className="space-y-3 font-bold">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Roster Size (Players)</label>
              <Input type="number" value={rosterSize} onChange={e => setRosterSize(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Tournaments</label>
                <Input type="number" value={numTournaments} onChange={e => setNumTournaments(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Avg Fee ($)</label>
                <Input type="number" value={avgTourneyFee} onChange={e => setAvgTourneyFee(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Uniform Package per Swimmer/Player ($)</label>
              <Input type="number" value={uniformCost} onChange={e => setUniformCost(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Facility Rent / Pool Rental ($)</label>
              <Input type="number" value={facilityRent} onChange={e => setFacilityRent(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Coach Travel ($)</label>
                <Input type="number" value={coachStipend} onChange={e => setCoachStipend(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Fundraising Offset ($)</label>
                <Input type="number" value={fundraisingGoal} onChange={e => setFundraisingGoal(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
              </div>
            </div>

          </div>
        </div>

        {/* Output metrics */}
        <div className="lg:col-span-2 space-y-6">
          
          <div className="grid grid-cols-3 gap-4 font-mono font-bold">
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Total Budget</span>
              <span className="text-2xl font-black font-display text-black dark:text-white">${totalExpense.toLocaleString()}</span>
            </div>
            <div className="bg-[#F5F5F0] border-4 border-black p-4 text-center dark:bg-slate-900">
              <span className="text-[10px] uppercase block text-gray-500 mb-1">Fundraising offset</span>
              <span className="text-2xl font-black font-display text-green-600">${fundGoal.toLocaleString()}</span>
            </div>
            <div className="bg-[#FFD600] border-4 border-black p-4 text-center text-black">
              <span className="text-[10px] uppercase block text-black/70 mb-1">Season Player Dues</span>
              <span className="text-3xl font-black font-display">${Math.round(perPlayerFee).toLocaleString()}</span>
              <span className="text-[9px] block text-black/70 mt-1 uppercase">PER PLAYER (N={players})</span>
            </div>
          </div>

          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
            <h4 className="font-display font-black text-xl uppercase tracking-tight border-b-4 border-black pb-2 dark:border-white">
              Budget Modeling Summary
            </h4>

            <div className="grid sm:grid-cols-2 gap-4 text-xs font-mono font-bold">
              <div className="space-y-3">
                 <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500 uppercase">Tournaments Fee ({tourneys} events):</span>
                    <span>${tourneyExpense.toLocaleString()}</span>
                 </div>
                 <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500 uppercase">Uniforms Package ({players} sets):</span>
                    <span>${uniformsExpense.toLocaleString()}</span>
                 </div>
              </div>
              <div className="space-y-3">
                 <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500 uppercase">Rentals & Stipends:</span>
                    <span>${(rent + coach).toLocaleString()}</span>
                 </div>
                 <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500 uppercase">Net Cost to Cover:</span>
                    <span>${netExpense.toLocaleString()}</span>
                 </div>
              </div>
            </div>

            {/* Break-even roster comparisons */}
            <div className="pt-4 border-t-2 border-black space-y-4">
              <h5 className="font-display font-black text-base uppercase tracking-tight">Roster Ratios Comparison</h5>
              <p className="text-xs text-gray-500 font-mono leading-normal">Check how roster counts affect individual player registration dues. Fixed facility costs distribute thinner as team size increases.</p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F5F5F0] border-b-2 border-black font-mono font-bold uppercase tracking-wider text-black dark:bg-slate-900 dark:border-white dark:text-white">
                      <th className="py-2 px-3">Simulated Roster Size</th>
                      <th className="py-2 px-3">Total Operational Budget</th>
                      <th className="py-2 px-3 text-right">Computed Dues Per Player</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y font-bold">
                    {breakEvenScenarios.map(sc => (
                      <tr key={sc.size} className={`hover:bg-[#FFD600]/10 transition-colors ${sc.size === players ? 'bg-[#FFD600]/20' : ''}`}>
                        <td className="py-2 px-3 font-mono">
                          {sc.size} Players {sc.size === players && <span className="bg-black text-[#FFD600] px-1 text-[8px] font-bold uppercase ml-2 border border-black font-mono">Selected</span>}
                        </td>
                        <td className="py-2 px-3 font-mono">${sc.total.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right font-mono text-base text-[#0044FF]">${Math.round(sc.perPlayer).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}
